import {
  ArrowLeftIcon,
  Button,
  FilterIcon,
  QuantityStepper,
} from '@tovala/component-library'
import { clsx } from 'clsx'
import { compact, flatMap, intersection, isEmpty, pickBy } from 'lodash-es'
import {
  Listing,
  MealSummary,
  MenuListingsLayout,
  OrderEstimate,
  queries,
  useActiveIncentives,
  useAddSelection,
  useCustomer,
  useInvalidateMeals,
  useInvalidateOrderEstimate,
  useOrderEstimate,
  useMealReviewSummaries,
  useMealsInFilters,
  useMealSortInfo,
  useMealSummaries,
  useRemoveSelection,
  useUpsertUserTermStatus,
  useUserMenuAttributes,
  Meal,
} from '@tovala/browser-apis-combinedapi'
import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { fromPromise } from 'xstate'
import { useMachine } from '@xstate/react'
import { useNavigate } from 'react-router-dom'

import { events, sourceIDs } from '../../../analytics/events'
import { menuMachine } from './machines/menu'
import { isCombinedAPIResponseError } from 'utils/api'
import {
  getMealSelectionIDs,
  getMealSurcharge,
  TAG_IDS,
  isMealSoldOutError,
} from 'utils/meals'
import { hasMealTag } from 'utils/mealTags'
import {
  collapseEverythingElseMeals,
  consumeCheckoutHandoff,
  consumePendingRecommendedMealFilters,
  getFilteredAllergenMealSelections,
  getHasSavedMealFiltersForUser,
  getHasSelectedFilters,
  getIsBlueprintAttributedUser,
  getMealGroups,
  getMealIDsInFilters,
  getMealIndex,
  getMealSortOptions,
  getRecommendedSelectedMealFilters,
  getSelectedMealFilters,
  getSelectedMealFiltersCount,
  getSelectedMealFiltersMetadata,
  migrateLegacyMealFilters,
  getShowBlueprintFirstOrderMenu,
  getSortedMealIDs,
  getValidSelectedMealFilters,
  saveSelectedMealFilters,
} from 'utils/menus'
import { isTermDelivering } from 'utils/terms'
import { getEnvVar } from 'utils/env'
import { getTotalCreditsCents } from 'utils/user'
import {
  SelectedMealFilters,
  SelectionEventSourceID,
  UserTerm,
} from 'types/internal'
import { storageAvailable } from 'utils/storageAvailable'
import { track } from 'utils/analytics'
import { wrapWithContactSupportTeam } from 'utils/errors'
import { getHasViewedFamilyMealsWarning } from './FamilyMealConfirmationDialog'
import { getSelectedFilterTheme } from './filterThemes'

import { useDetailsDialog } from 'components/common/details/hooks'
import { useIsNoMenuTerm } from 'hooks/menus'
import { useFeatures } from 'contexts/features'
import { useFirstOvenCookHistory } from 'hooks/cookHistory'
import { useOrderHistory } from 'hooks/orderHistory'
import {
  useMenuProductRatings,
  useResolvedMenuComponents,
  useScrollToMenuTopOnFiltersChange,
  useTrackScrollDepth,
} from './hooks'
import { useToast } from 'contexts/toast'
import { useUser } from 'contexts/user'
import ConfirmationDialogs, {
  ConfirmationDialogData,
} from './ConfirmationDialogs'
import CreditsBanner from './CreditsBanner'
import ExtrasAllergenNote from './ExtrasAllergenNote'
import ExtrasGrid from './ExtrasGrid'
import CustomerIOInlineContainer from 'components/common/CustomerIOInlineContainer'
import ListingDetailsDialog from 'components/common/details/ListingDetailsDialog'
import MealDetailsDialog from 'components/common/details/MealDetailsDialog'
import MealRatingBadge from './MealRatingBadge'
import MealReviewCard from './MealReviewCard'
import MealSort from './MealSort'
import MenuComponentsGrid from './MenuComponentsGrid'
import MenuError from './MenuError'
import MenuFilters from './MenuFilters'
import MenuFixedBottomContainer from './MenuFixedBottomContainer'
import MenuHeader from './MenuHeader'
import MenuMeals from './MenuMeals'
import MenuTabGroup, { useMenuTabs } from './MenuTabGroup'
import MenuUnavailable from './MenuUnavailable'
import OnBreakBanner from './OnBreakBanner'
import OrderStatus from './OrderStatus'
import OrderSummarySidebar, {
  OrderSummaryExtra,
  OrderSummaryMeal,
  useOrderSummarySidebar,
} from './OrderSummarySidebar'
import PromosCreditsModal from '../PromosCreditsModal'
import RateMealsPrompt from '../rateMeals/RateMealsPrompt'
import { useMostRecentTermMealsToRate } from '../rateMeals/hooks'
import SpecialEventBanner from './SpecialEventBanner'

const Menu = ({
  menuListingsLayout,
  onEditTermStatus,
  selectedUserTerm,
}: {
  menuListingsLayout: MenuListingsLayout
  onEditTermStatus(userTerm: UserTerm): void
  selectedUserTerm: UserTerm
}) => {
  const navigate = useNavigate()
  const { openToast } = useToast()

  const features = useFeatures()
  const { user } = useUser()

  const menuTabs = useMenuTabs()
  const selectedMenuTab = menuTabs.tabs[menuTabs.selectedIndex]

  const { data: customer } = useCustomer({
    customerID: user.subscription.customerID,
    userID: user.id,
  })

  const hasPaymentSource = !!(customer && customer.sources.data.length > 0)

  const isSubscriptionActive = user.subscription.status === 'active'

  const { data: orderAgainMeals = [] } = useQuery({
    ...queries.orderAgain.list({
      userID: user.id,
      menuID: selectedUserTerm.selectedSubTerm?.mainMenu.id,
    }),
    select: (data) => data.meals,
  })

  const { ratingsByMealID } = useMenuProductRatings({
    menuID: selectedUserTerm.selectedSubTerm?.mainMenu.id,
  })

  const { data: activeIncentives = [] } = useActiveIncentives({
    userID: user.id,
  })

  const { data: userMenuAttributes } = useUserMenuAttributes({
    userID: user.id,
  })

  const { data: ovenCookHistory = [] } = useFirstOvenCookHistory({
    userID: user.id,
  })

  const { invalidateMealSummaries } = useInvalidateMeals()
  const { invalidateOrderEstimate } = useInvalidateOrderEstimate()

  const { data: mealReviewSummaries } = useMealReviewSummaries({
    userID: user.id,
  })

  const { data: mealSummaries = [] } = useMealSummaries({
    subTermID: selectedUserTerm.selectedSubTermID,
  })

  const {
    closeDetailsDialog,
    detailsType,
    listing,
    meal,
    openDetailsDialog,
    shouldScrollToReview,
  } = useDetailsDialog({
    listings: flatMap(menuListingsLayout.sections, (section) => {
      return section ? section.listings : []
    }),
    termID: selectedUserTerm.termID,
  })

  const { receipts, totalOrderCount } = useOrderHistory({ user })

  const mostRecentTermMealsToRate = useMostRecentTermMealsToRate({
    cookHistory: ovenCookHistory,
    mealReviewSummaries,
    orderHistoryReceipts: receipts,
  })

  const {
    data: mealFilters = [],
    isPending: isLoadingMealFilters,
    error: loadMealFiltersError,
    isError: hasLoadMealFiltersError,
  } = useQuery(queries.filters.meals())
  const { data: mealsInFilters = [], isPending: isLoadingMealsInFilters } =
    useMealsInFilters({
      subTermID: selectedUserTerm.selectedSubTermID,
    })
  const { data: mealSortInfo } = useMealSortInfo({
    termID: selectedUserTerm.termID,
  })
  const menuSortOptions = getMealSortOptions({ mealSortInfo })

  const sortedMealIDs = getSortedMealIDs({ mealSortInfo })

  const menuHasListings =
    menuListingsLayout.sections &&
    menuListingsLayout.sections.some((section) => section?.listings.length > 0)
  const { data: orderEstimate } = useOrderEstimate({
    termID: isTermDelivering(selectedUserTerm)
      ? selectedUserTerm.termID
      : undefined,
    userID: user.id,
  })

  const listingsSelections = orderEstimate?.currentExtras ?? []
  const listingsSelectionIDs = listingsSelections.map(
    (listing) => listing.listingID,
  )
  const selectedListings = flatMap(menuListingsLayout.sections, (section) => {
    return section ? section.listings : []
  }).filter((listing) => {
    return listingsSelectionIDs.includes(listing.id)
  })

  const [confirmationDialogData, setConfirmationDialogData] =
    useState<ConfirmationDialogData | null>(null)

  const [selectedMealFilters, setSelectedMealFilters] =
    useState<SelectedMealFilters>(() => {
      migrateLegacyMealFilters({ userID: user.id })

      return getSelectedMealFilters({ userID: user.id })
    })

  useEffect(() => {
    if (
      mealFilters.length === 0 ||
      mealsInFilters.length === 0 ||
      mealSummaries.length === 0
    ) {
      return
    }

    const storedSelectedMealFilters = getSelectedMealFilters({
      userID: user.id,
    })

    if (!isEmpty(storedSelectedMealFilters)) {
      const validStoredFilters = getValidSelectedMealFilters({
        mealFilters,
        selectedMealFilters: storedSelectedMealFilters,
      })
      setSelectedMealFilters(validStoredFilters)
    }

    if (!userMenuAttributes) {
      return
    }

    // Both triggers are spent here whatever the outcome, so recommended filters
    // are applied once per arrival rather than on every menu load. Re-applying
    // on a later login is intended; the guard below is only that the user has no
    // filters of their own. Assign both before testing them, since "||" would
    // leave the second one armed.
    const hasCheckoutHandoff = consumeCheckoutHandoff({ userID: user.id })
    const hasPendingRecommendedMealFilters =
      consumePendingRecommendedMealFilters()

    if (!hasCheckoutHandoff && !hasPendingRecommendedMealFilters) {
      return
    }

    if (getHasSavedMealFiltersForUser({ userID: user.id })) {
      return
    }

    const recommendedMealFilters = getRecommendedSelectedMealFilters({
      mealFilters,
      recommendedFilters: userMenuAttributes.recommendedFilters,
    })

    if (getHasSelectedFilters(recommendedMealFilters)) {
      setSelectedMealFilters(recommendedMealFilters)
      saveSelectedMealFilters(recommendedMealFilters, user.id)

      const matchingMealCount = getMealIDsInFilters(
        recommendedMealFilters,
        mealFilters,
        mealsInFilters,
        mealSummaries.map(({ id }) => id),
      ).length

      compact(Object.values(recommendedMealFilters).flat()).forEach(
        (filterKey) => {
          track(events.ENABLES_FILTER, {
            filter_id: filterKey,
            matching_meal_count: matchingMealCount,
          })
        },
      )
    }
  }, [mealFilters, mealSummaries, mealsInFilters, user.id, userMenuAttributes])

  const [hasExpandedEverythingElse, setHasExpandedEverythingElse] =
    useState(false)
  const [selectedMealSort, setSelectedMealSort] = useState<string>('featured')
  const [isFiltersSidebarOpen, setIsFiltersSidebarOpen] = useState(false)
  const [showRateMealsPrompt, setShowRateMealsPrompt] = useState(true)
  const [showPromoCreditsModal, setShowPromoCreditsModal] = useState(false)

  useScrollToMenuTopOnFiltersChange({
    isFiltersSidebarOpen,
    scrollToMenuTop: menuTabs.scrollToMenuTop,
    selectedMealFilters,
  })

  const { closeOrderSummary, openOrderSummary, orderSummarySidebarStatus } =
    useOrderSummarySidebar({
      hasSelectedAllMeals: selectedUserTerm.hasSelectedAllMeals,
    })

  const [state, send] = useMachine(
    menuMachine.provide({
      actions: {
        showAddListingError: (_, { data: err }) => {
          if (err instanceof Error && isCombinedAPIResponseError(err)) {
            if (err.response?.data.message === 'listing is sold out') {
              openToast({
                goodErrorMessaging: {
                  wayOut: null,
                  whatHappened: 'Extra is sold out',
                  why: "We couldn't add this extra because it's sold out.",
                },
                type: 'error',
              })

              return
            }
          }

          openToast({
            goodErrorMessaging: {
              helpToFix: 'Please try adding the extra again.',
              whatHappened: 'Unable to add extra',
              why: "We couldn't add this extra due to a technical issue on our end.",
            },
            type: 'error',
          })
        },
        showAddMealError: (_, { data: err }) => {
          if (err instanceof Error && isCombinedAPIResponseError(err)) {
            if (err.response?.data.message === 'BlackSheetTrayRequired') {
              openToast({
                goodErrorMessaging: {
                  helpToFix: 'Please try again.',
                  wayOut: wrapWithContactSupportTeam(
                    'If you need further assistance',
                  ),
                  whatHappened: 'Unable to add meal',
                  why: "We couldn't add this meal because it requires a Tovala Sheet Tray and your account does not indicate you have one.",
                },
                type: 'error',
              })

              return
            } else if (isMealSoldOutError(err)) {
              openToast({
                goodErrorMessaging: {
                  wayOut: null,
                  whatHappened: 'Meal is sold out',
                  why: "We couldn't add this meal because it's sold out.",
                },
                type: 'error',
              })

              return
            } else if (err.response?.data.message === 'UserSkippedTerm') {
              openToast({
                goodErrorMessaging: {
                  wayOut:
                    'Please unskip this week and try adding the meal again.',
                  whatHappened: 'Week Skipped',
                  why: "We couldn't add this meal because you've skipped this week.",
                },
                type: 'error',
              })

              return
            }
          }

          openToast({
            goodErrorMessaging: {
              helpToFix: 'Please try adding the meal again.',
              whatHappened: 'Unable to add meal',
              why: "We couldn't add this meal due to a technical issue on our end.",
            },
            type: 'error',
          })
        },
        showRemoveListingError: () => {
          openToast({
            goodErrorMessaging: {
              helpToFix: 'Please try removing the extra again.',
              whatHappened: 'Unable to remove extra',
              why: "We couldn't remove this extra due to a technical issue on our end.",
            },
            type: 'error',
          })
        },
        showRemoveMealError: () => {
          openToast({
            goodErrorMessaging: {
              helpToFix: 'Please try removing the meal again.',
              whatHappened: 'Unable to remove meal',
              why: "We couldn't remove this meal due to a technical issue on our end.",
            },
            type: 'error',
          })
        },
        trackConfirmedUpgrade: () => {
          track(events.USER_CONFIRMED_UPSELL)
        },
        trackFinishedMealSelection: (_, { termID }) => {
          track(events.FINISHES_MEAL_SELECTION, {
            term_id: termID,
          })
        },
        trackMealAdded: (_, { addMealMeta, termID, orderEstimate }) => {
          if (addMealMeta) {
            trackMealModified({
              maxSelections: orderEstimate.mealSubscriptionCount ?? 0,
              mealID: addMealMeta.mealID,
              mealIndex: addMealMeta.mealIndex,
              numSelections: orderEstimate.mealSelections.length,
              rating: ratingsByMealID.get(addMealMeta.mealID)?.stars ?? null,
              termID,
            })
          }
        },
        trackMealRemoved: (_, { removeMealMeta, termID, mealSelections }) => {
          if (removeMealMeta) {
            trackMealModified({
              maxSelections: removeMealMeta.maxSelections,
              mealID: removeMealMeta.mealID,
              mealIndex: removeMealMeta.mealIndex,
              numSelections: mealSelections.length,
              rating: ratingsByMealID.get(removeMealMeta.mealID)?.stars ?? null,
              termID,
            })
          }
        },
      },
      guards: {
        hasFewerMealsThanNextPlanMax: () => {
          return (
            !!selectedUserTerm.nextLargestSubscriptionType &&
            selectedUserTerm.nextLargestSubscriptionType.maxSelections >
              mealSelectionIDs.length
          )
        },
        hasFewerThanMaxMeals: () => {
          return selectedUserTerm.nextLargestSubscriptionType !== null
        },
        hasMaxMeals: (_, { orderEstimate }) => {
          const numMealsToAdd =
            orderEstimate.mealSubscriptionCount -
            orderEstimate.mealSelections.length

          return numMealsToAdd === 0
        },
        hasSelectedAllMeals: () => {
          return selectedUserTerm.hasSelectedAllMeals
        },
      },
      actors: {
        addListing: fromPromise(async ({ input }) => {
          const { listingID, userID } = input

          return addSelectionAsync({
            data: {
              itemID: listingID,
              itemType: 'listing',
            },
            subTermID: selectedUserTerm.selectedSubTermID,
            userID,
          })
        }),
        addMeal: fromPromise(async ({ input }) => {
          const { mealID, userID } = input

          return addSelectionAsync({
            data: {
              itemID: mealID.toString(),
              itemType: 'meal',
            },
            subTermID: selectedUserTerm.selectedSubTermID,
            userID,
          })
        }),
        removeListing: fromPromise(async ({ input }) => {
          const { listingID, userID } = input

          return removeSelectionAsync({
            data: {
              itemID: listingID,
              itemType: 'listing',
            },
            subTermID: selectedUserTerm.selectedSubTermID,
            userID,
          })
        }),
        removeMeal: fromPromise(async ({ input }) => {
          const { mealID, userID } = input

          return removeSelectionAsync({
            data: {
              itemID: mealID.toString(),
              itemType: 'meal',
            },
            subTermID: selectedUserTerm.selectedSubTermID,
            userID,
          })
        }),
        updateTermStatus: fromPromise(async () => {
          if (!selectedUserTerm.nextLargestSubscriptionType) {
            throw new Error('No next subscription type for upgrade.')
          }

          return await upsertUserTermStatus(
            {
              data: {
                notes: '',
                subscriptionTypeID:
                  selectedUserTerm.nextLargestSubscriptionType.id,
                subTermID: selectedUserTerm.selectedSubTermID,
                termID: selectedUserTerm.termID,
                userID: user.id,
              },
              termStatusID: selectedUserTerm.id ?? '',
              userID: user.id,
            },
            {
              onSuccess: () => {
                handleInvalidateOrderEstimate()
              },
            },
          )
        }),
        savePendingMeal: fromPromise(async ({ input }) => {
          const { pendingMealIDs, nextPendingMealIndex } = input

          return await addSelectionAsync({
            data: {
              itemID: pendingMealIDs[nextPendingMealIndex].toString(),
              itemType: 'meal',
            },
            subTermID: selectedUserTerm.selectedSubTermID,
            userID: user.id,
          })
        }),
      },
    }),
    {
      input: {
        subTermID: selectedUserTerm.selectedSubTermID,
        termID: selectedUserTerm.termID,
        userID: user.id,
      },
    },
  )

  const { nextMaxSelections, pendingMealIDs } = state.context
  const pendingMealSelections = compact(
    pendingMealIDs.map((mealID) => {
      return mealSummaries.find(({ id }) => id === mealID)
    }),
  )
  const mealSelectionIDs = getMealSelectionIDs({
    pendingMealSelections,
    selectedUserTerm,
  })
  const selectedMeals = mealSummaries.filter((meal) => {
    return mealSelectionIDs.includes(meal.id)
  })

  const {
    error: upsertUserTermStatusError,
    isError: hasUpsertUserTermStatusError,
    mutateAsync: upsertUserTermStatus,
  } = useUpsertUserTermStatus()

  const handleInvalidateOrderEstimate = ({
    newOrderEstimate,
  }: {
    newOrderEstimate?: OrderEstimate
  } = {}) => {
    invalidateOrderEstimate({
      termID: selectedUserTerm.termID,
      userID: user.id,
      ...(newOrderEstimate && { opts: { newOrderEstimate } }),
    })
  }

  const { mutateAsync: addSelectionAsync } = useAddSelection({
    onSuccess: (...args) => {
      handleInvalidateOrderEstimate({ newOrderEstimate: args[0] })
    },

    onError: (...args) => {
      const err = args[0]
      if (err instanceof Error && isMealSoldOutError(err)) {
        invalidateMealSummaries(selectedUserTerm.selectedSubTermID)
      }
    },
  })

  const { mutateAsync: removeSelectionAsync } = useRemoveSelection({
    onSuccess: (...args) => {
      handleInvalidateOrderEstimate({ newOrderEstimate: args[0] })
    },
  })

  const showOrderUpgrade =
    state.matches('upgrading') || pendingMealIDs.length > 0
  const isUpgrading = state.matches({ upgrading: 'savingUpgrades' })
  const isUpdatingListingSelections =
    state.matches('addingListing') || state.matches('removingListing')
  const isUpdatingMealSelections =
    state.matches('addingMeal') ||
    state.matches('removingMeal') ||
    state.matches({ upgrading: 'removingMeal' })
  const canDecrementListing = !isUpdatingListingSelections
  const canDecrementMeal = !isUpdatingMealSelections
  const canAddMoreMealsForUpgrade =
    !!selectedUserTerm.nextLargestSubscriptionType &&
    mealSelectionIDs.length <
      selectedUserTerm.nextLargestSubscriptionType.maxSelections
  const canIncrementListing = !isUpdatingListingSelections
  const canIncrementMeal =
    (!selectedUserTerm.hasSelectedAllMeals || canAddMoreMealsForUpgrade) &&
    !isUpdatingMealSelections

  const handleMealSelectionConfirmationDialogs = async ({
    onConfirm,
    meal,
  }: {
    onConfirm(): void
    meal: MealSummary
  }) => {
    if (
      features.showFamilyMealsWarning &&
      hasMealTag(meal.tags, TAG_IDS.FAMILY) &&
      !getHasViewedFamilyMealsWarning()
    ) {
      setConfirmationDialogData({ meal, onConfirm, type: 'familyMealsWarning' })
      return
    }

    onConfirm()
  }

  const incrementListing = ({
    listingID,
    selectionEventSourceID,
  }: {
    listingID: string
    selectionEventSourceID: SelectionEventSourceID
  }) => {
    send({
      listingID,
      selectionEventSourceID,
      type: 'ADD_LISTING',
    })
  }

  const onClickIncrementListing = ({
    coupledMealID,
    index,
    listing,
    onClickEventSourceID = sourceIDs.EXTRAS_SCREEN,
  }: {
    coupledMealID?: number
    index?: number
    listing: Listing
    onClickEventSourceID?: SelectionEventSourceID
  }) => {
    const { enabled_filters, enabled_filters_count } =
      getSelectedMealFiltersMetadata({ userID: user.id })

    track(events.DID_TAP_EXTRA_INCREMENT, {
      listing_id: listing.id,
      source_id: onClickEventSourceID,
      index,
      coupled_meal_id: coupledMealID,
      enabled_filters,
      enabled_filters_count,
      sort_order: selectedMealSort,
    })

    let extrasNotFilteredViewed: string | null = null

    if (storageAvailable('localStorage')) {
      extrasNotFilteredViewed = localStorage.getItem('extrasNotFilteredViewed')
    }

    if (!isEmpty(selectedMealFilters) && !extrasNotFilteredViewed) {
      if (storageAvailable('localStorage')) {
        localStorage.setItem('extrasNotFilteredViewed', 'true')
      }

      setConfirmationDialogData({
        onConfirm: () => {
          incrementListing({
            listingID: listing.id,
            selectionEventSourceID: onClickEventSourceID,
          })
          setConfirmationDialogData(null)
        },
        type: 'noListingFilters',
      })
    } else {
      incrementListing({
        listingID: listing.id,
        selectionEventSourceID: onClickEventSourceID,
      })
    }
  }

  const incrementMeal = ({
    meal,
    selectionEventSourceID,
  }: {
    meal: MealSummary
    selectionEventSourceID: SelectionEventSourceID
  }) => {
    send({
      mealID: meal.id,
      mealIndex: getMealIndex({ mealGroups, mealID: meal.id }),
      selectionEventSourceID,
      nextMaxSelections:
        selectedUserTerm.nextLargestSubscriptionType?.maxSelections,
      type: 'ADD_MEAL',
    })
  }

  const onClickIncrementMeal = async ({
    meal,
    onClickEventSourceID = sourceIDs.MENU_TERM,
  }: {
    meal: MealSummary
    onClickEventSourceID?: SelectionEventSourceID
  }) => {
    const { enabled_filters, enabled_filters_count } =
      getSelectedMealFiltersMetadata({ userID: user.id })

    track(events.DID_TAP_MEAL_INCREMENT, {
      meal_id: meal.id,
      rating: ratingsByMealID.get(meal.id)?.stars ?? null,
      source_id: onClickEventSourceID,
      term_id: selectedUserTerm.termID,
      enabled_filters,
      enabled_filters_count,
      sort_order: selectedMealSort,
      ...(hasSelectedFilters && {
        in_filtered_section: mealIDsInFilters.includes(meal.id)
          ? 'true'
          : 'false',
      }),
      has_ordered_previously: orderAgainMeals.some(
        (orderAgainMeal) => orderAgainMeal.id === meal.id,
      )
        ? 'true'
        : 'false',
    })

    handleMealSelectionConfirmationDialogs({
      meal,
      onConfirm: () => {
        incrementMeal({
          meal,
          selectionEventSourceID: onClickEventSourceID,
        })
      },
    })
  }

  const decrementListing = ({
    listingID,
    selectionEventSourceID,
  }: {
    listingID: string
    selectionEventSourceID: SelectionEventSourceID
  }) => {
    send({
      listingID,
      selectionEventSourceID,
      type: 'REMOVE_LISTING',
    })
  }

  const onClickDecrementListing = ({
    coupledMealID,
    index,
    listing,
    onClickEventSourceID = sourceIDs.EXTRAS_SCREEN,
  }: {
    coupledMealID?: number
    index?: number
    listing: Listing
    onClickEventSourceID?: SelectionEventSourceID
  }) => {
    const { enabled_filters, enabled_filters_count } =
      getSelectedMealFiltersMetadata({ userID: user.id })

    track(events.DID_TAP_EXTRA_DECREMENT, {
      listing_id: listing.id,
      source_id: onClickEventSourceID,
      index,
      coupled_meal_id: coupledMealID,
      enabled_filters,
      enabled_filters_count,
      sort_order: selectedMealSort,
    })

    if (listing.isSoldOut) {
      setConfirmationDialogData({
        listing,
        onConfirm: () => {
          decrementListing({
            listingID: listing.id,
            selectionEventSourceID: onClickEventSourceID,
          })
        },
        type: 'releaseListingFromCart',
      })
      return
    }

    decrementListing({
      listingID: listing.id,
      selectionEventSourceID: onClickEventSourceID,
    })
  }

  const decrementMeal = ({
    mealID,
    selectionEventSourceID,
  }: {
    mealID: number
    selectionEventSourceID: SelectionEventSourceID
  }) => {
    const quantity = mealSelectionIDs.filter((x) => x === mealID).length

    if (selectedUserTerm.subscriptionType && quantity > 0) {
      send({
        maxSelections: selectedUserTerm.subscriptionType.maxSelections,
        mealID,
        mealIndex: getMealIndex({ mealGroups, mealID }),
        selectionEventSourceID,
        type: 'REMOVE_MEAL',
      })
    }
  }

  const onClickDecrementMeal = ({
    meal,
    onClickEventSourceID = sourceIDs.MENU_TERM,
  }: {
    meal: MealSummary
    onClickEventSourceID?: SelectionEventSourceID
  }) => {
    const { enabled_filters, enabled_filters_count } =
      getSelectedMealFiltersMetadata({ userID: user.id })

    track(events.DID_TAP_MEAL_DECREMENT, {
      meal_id: meal.id,
      rating: ratingsByMealID.get(meal.id)?.stars ?? null,
      source_id: onClickEventSourceID,
      term_id: selectedUserTerm.termID,
      enabled_filters,
      enabled_filters_count,
      sort_order: selectedMealSort,
      ...(hasSelectedFilters && {
        in_filtered_section: mealIDsInFilters.includes(meal.id)
          ? 'true'
          : 'false',
      }),
      has_ordered_previously: orderAgainMeals.some(
        (orderAgainMeal) => orderAgainMeal.id === meal.id,
      )
        ? 'true'
        : 'false',
    })

    if (meal.isSoldOut) {
      setConfirmationDialogData({
        meal,
        onConfirm: () => {
          decrementMeal({
            mealID: meal.id,
            selectionEventSourceID: onClickEventSourceID,
          })
        },
        type: 'releaseFromCart',
      })
      return
    }

    decrementMeal({
      mealID: meal.id,
      selectionEventSourceID: onClickEventSourceID,
    })
  }

  // Outside actions can change whether the user has all their meals selected,
  // such as editing their subscription type to be a smaller or larger number of
  // meals. Both directions need to be sent so the machine doesn't stay in a
  // selections state that no longer matches the term.
  useEffect(() => {
    send({
      type: selectedUserTerm.hasSelectedAllMeals
        ? 'ALL_MEALS_SELECTED'
        : 'NOT_ALL_MEALS_SELECTED',
    })
  }, [send, selectedUserTerm.hasSelectedAllMeals])

  const onChangeSelectedMealFilters = (
    selectedMealFilters: SelectedMealFilters,
  ) => {
    const filters = pickBy(selectedMealFilters, (value) => {
      return value && value.length > 0
    })
    setSelectedMealFilters(filters)

    saveSelectedMealFilters(filters, user.id)

    handleMealSelectionsOutsideFilters({ selectedMealFilters: filters })
  }

  const onChangeSelectedMealSort = (sortOption: string) => {
    setSelectedMealSort(sortOption)
  }

  const handleMealSelectionsOutsideFilters = ({
    selectedMealFilters,
  }: {
    selectedMealFilters: SelectedMealFilters
  }) => {
    const filteredAllergenMealSelections = getFilteredAllergenMealSelections({
      selectedMealIDs: selectedUserTerm.mealSelections.map(
        (mealSelection) => mealSelection.mealID,
      ),
      mealsInFilters,
      selectedMealFilters,
    })

    if (filteredAllergenMealSelections.length > 0) {
      setConfirmationDialogData({
        type: 'filteredAllergens',
      })
    }
  }

  useTrackScrollDepth({
    maxSelections: selectedUserTerm.subscriptionType?.maxSelections,
    numMeals: mealSummaries.length,
    numSelections: selectedUserTerm.mealSelections.length,
    selectedMealSort,
    termID: selectedUserTerm.termID,
    userID: user.id,
  })

  const { loadMenuError, resolvedMenuComponents } = useResolvedMenuComponents({
    enableMenuTargeting: features.enableMenuTargeting,
    mealSelectionIDs,
    listings: menuListingsLayout.sections.flatMap(
      (section) => section.listings,
    ),
    mealSummaries,
    mealSwaps: selectedUserTerm.mealSwaps,
    orderAgainMeals,
    ratingsByMealID,
    subTermID: selectedUserTerm.selectedSubTermID,
    userID: user.id,
  })

  const hasOrderAgainCarousel = resolvedMenuComponents.some(
    (component) => component.type === 'orderAgainCarousel',
  )

  useEffect(() => {
    if (hasOrderAgainCarousel) {
      track(events.ORDER_AGAIN_CAROUSEL_SHOWN, {
        term_id: selectedUserTerm.termID,
      })
    }
  }, [hasOrderAgainCarousel, selectedUserTerm.termID])

  const hasSelectedFilters = getHasSelectedFilters(selectedMealFilters)
  const selectedFiltersCount = getSelectedMealFiltersCount(selectedMealFilters)

  const filteredGroupTheme = getSelectedFilterTheme({
    isEnabled: features.showColoredLongevityFilterResults,
    selectedMealFilters,
  })

  const hasFlushMealsTop = !!filteredGroupTheme

  const allMealIDs = mealSummaries.map(({ id }) => id)

  const { mealGroups, mealIDsInFilters } = getMealGroups({
    filtersData: hasSelectedFilters
      ? {
          allMealIDs,
          listingsInFilters: menuListingsLayout.filters,
          selectedMealFilters,
          mealFilters,
          mealsInFilters,
        }
      : null,
    menuComponents: resolvedMenuComponents,
    sortData: {
      menuSortOptions,
      selectedMealSort,
      sortedMealIDs,
    },
  })

  const isBlueprintAttributedUser = getIsBlueprintAttributedUser({
    userAcquisitionType: userMenuAttributes?.userAcquisitionType,
  })

  // Expanding is one way: once a Blueprint user asks to see the rest of the
  // menu, they keep it for the session even if they re-apply the filter.
  const shouldCollapseEverythingElse =
    !hasExpandedEverythingElse &&
    getShowBlueprintFirstOrderMenu({
      isBlueprintAttributedUser,
      selectedMealFilters,
      totalOrderCount,
    })

  const collapsedEverythingElse = shouldCollapseEverythingElse
    ? collapseEverythingElseMeals({ mealGroups })
    : null

  const everythingElseCollapse = collapsedEverythingElse?.hiddenMealsCount
    ? collapsedEverythingElse
    : null

  const hiddenMealsCount = everythingElseCollapse?.hiddenMealsCount ?? 0
  const displayMealGroups = everythingElseCollapse?.mealGroups ?? mealGroups

  const onClickShowAllMeals = () => {
    setHasExpandedEverythingElse(true)

    const { enabled_filters, enabled_filters_count } =
      getSelectedMealFiltersMetadata({ userID: user.id })

    track(events.MENU_FILTERS_EVERYTHINGELSE_SHOWALL, {
      term_id: selectedUserTerm.termID,
      meal_count: hiddenMealsCount,
      enabled_filters,
      enabled_filters_count,
    })
  }

  const isLoadingNeededFilters =
    hasSelectedFilters && (isLoadingMealFilters || isLoadingMealsInFilters)

  // If the user has selected filters, we want to wait until the filter data
  // loads so we avoid a flash of inaccurate meal displays (in the case where
  // meal summaries load before filter data does).
  const canDisplayMenu =
    selectedUserTerm.readyForView &&
    mealSummaries.length > 0 &&
    !isLoadingNeededFilters

  const canModifySelections =
    !selectedUserTerm.isSkipped && !selectedUserTerm.isOnBreak

  const commonMenuComponentGridProps = {
    canDecrement: canDecrementMeal,
    canIncrement: canIncrementMeal,
    canModifySelections,
    listingsSelections,
    mealSelectionIDs,
    mealSelectionMax: selectedUserTerm.subscriptionType?.maxSelections,
    onClickDecrement: (
      opts:
        | {
            listing: Listing
          }
        | {
            meal: MealSummary
          },
    ) => {
      if ('listing' in opts) {
        onClickDecrementListing(opts)
      } else {
        onClickDecrementMeal(opts)
      }
    },
    onClickIncrement: (
      opts:
        | {
            listing: Listing
            onClickEventSourceID?:
              | 'extras_screen'
              | 'order_summary'
              | 'menu_term'
          }
        | {
            meal: MealSummary
          },
    ) => {
      if ('listing' in opts) {
        onClickIncrementListing(opts)
      } else {
        onClickIncrementMeal(opts)
      }
    },
    onClickMeal: (
      mealID: number,
      opts?: { shouldScrollToReview?: boolean },
    ) => {
      openDetailsDialog({
        type: 'meal',
        id: mealID,
        rating: ratingsByMealID.get(mealID)?.stars ?? null,
        shouldScrollToReview: opts?.shouldScrollToReview,
      })
    },
    onClickListing: (listingID: string, coupledMealID?: number) => {
      openDetailsDialog({ type: 'listing', id: listingID })

      if (coupledMealID !== undefined) {
        track(events.OPENS_EXTRA_DETAIL, {
          listing_id: listingID,
          coupled_meal_id: coupledMealID,
        })
      }
    },
    ratingsByMealID,
    selectedMealSort,
    termID: selectedUserTerm.termID,
    userID: user.id,
  }

  const commonOrderStatusProps = {
    closeOrderSummary,
    completesOrderFromMeals: isBlueprintAttributedUser,
    isSelectingListings: selectedMenuTab === 'extras',
    isUpgrading,
    listingsSelections,
    nextMaxSelections,
    onClickContinue: () => {
      menuTabs.selectTab('extras')
    },
    onClickDone: (openSource: 'orderSummary' | undefined = undefined) => {
      setConfirmationDialogData({
        isInfluencer: user.userTags.some(
          (userTag) => userTag.tag === 'influencer',
        ),
        openSource,
        type: 'mealsSelected',
      })
    },
    onClickSkipAdjust: () => {
      onEditTermStatus(selectedUserTerm)
    },
    onConfirmUpgrade: () => {
      send({ type: 'SAVE_PENDING_MEALS' })
    },
    openOrderSummary: () => {
      openOrderSummary()
    },
    orderSummarySidebarStatus,
    pendingMealSelections,
    selectedUserTerm,
    showOrderUpgrade,
    upgradeOrderError: hasUpsertUserTermStatusError
      ? upsertUserTermStatusError
      : null,
  }

  useEffect(() => {
    const isSkipped = selectedUserTerm.isSkipped
    const maxSelections = selectedUserTerm.subscriptionType?.maxSelections
    const selectionsCount = selectedUserTerm.mealSelections.length
    const { enabled_filters, enabled_filters_count } =
      getSelectedMealFiltersMetadata({ userID: user.id })

    track(events.OPENS_MENU, {
      term_id: selectedUserTerm.termID,
      meal_plan_state: isSkipped ? 'skipped' : 'active',
      meal_selection_count: isSkipped ? undefined : selectionsCount,
      meal_selection_max: isSkipped ? undefined : maxSelections,
      enabled_filters,
      enabled_filters_count,
      sort_order: selectedMealSort,
    })
    // we don't want to re-run this effect when the selected meal sort changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserTerm.termID])

  const { isNoMenuTerm, specialEvent } = useIsNoMenuTerm({
    term: selectedUserTerm,
  })

  if (isNoMenuTerm) {
    return (
      <NoMenuTerm message={specialEvent} selectedUserTerm={selectedUserTerm} />
    )
  }

  const termMealCount =
    showOrderUpgrade && selectedUserTerm.nextLargestSubscriptionType
      ? selectedUserTerm.nextLargestSubscriptionType.maxSelections
      : !showOrderUpgrade && selectedUserTerm.subscriptionType
        ? selectedUserTerm.subscriptionType.maxSelections
        : ''

  const commonFiltersProps = {
    allMealFilters: mealFilters,
    allMealIDs,
    hasLoadMealFiltersError,
    loadMealFiltersError,
    mealFilters,
    mealsInFilters,
    menuComponents: resolvedMenuComponents,
    menuID: selectedUserTerm.selectedSubTerm?.mainMenu.id,
    onChangeSelectedMealFilters,
    selectedMealFilters,
    sortData: {
      menuSortOptions,
      selectedMealSort,
      sortedMealIDs,
    },

    termID: selectedUserTerm.termID,
  }

  const openFiltersSidebar = () => {
    track(events.TAPS_FILTERS)
    setIsFiltersSidebarOpen(true)
  }

  const renderListingDetailsDialog = ({ listing }: { listing: Listing }) => {
    const quantity =
      listingsSelections.filter((s) => s.listingID === listing.id)?.length ?? 0

    const stepper =
      !canModifySelections || (listing.isSoldOut && quantity === 0) ? null : (
        <QuantityStepper
          disabledDecrement={!canDecrementListing}
          disabledIncrement={!canIncrementListing || listing.isSoldOut}
          labelDecrement="Remove Extra"
          labelIncrement="Add Extra"
          min={0}
          onClickDecrement={() => {
            onClickDecrementListing({
              listing,
              onClickEventSourceID: sourceIDs.DETAILS,
            })
          }}
          onClickIncrement={() => {
            onClickIncrementListing({
              listing,
              onClickEventSourceID: sourceIDs.DETAILS,
            })
          }}
          quantity={quantity}
          size="medium"
        />
      )
    return (
      <ListingDetailsDialog
        listing={listing}
        onClose={closeDetailsDialog}
        stepper={stepper}
      />
    )
  }

  const renderMealDetailsDialog = ({ meal }: { meal: Meal }) => {
    const mealSummary = mealSummaries.find((s) => s.id === meal.id)
    if (!mealSummary) {
      return null
    }

    const rating = ratingsByMealID.get(meal.id)

    const quantity = mealSelectionIDs.filter((x) => x === meal.id).length

    const stepper =
      !canModifySelections || (meal.isSoldOut && quantity === 0) ? null : (
        <QuantityStepper
          disabledDecrement={!canDecrementMeal}
          disabledIncrement={!canIncrementMeal || meal.isSoldOut}
          labelDecrement="Remove Meal"
          labelIncrement="Add Meal"
          min={0}
          onClickDecrement={() => {
            onClickDecrementMeal({
              meal: mealSummary,
              onClickEventSourceID: sourceIDs.DETAILS,
            })
          }}
          onClickIncrement={() => {
            onClickIncrementMeal({
              meal: mealSummary,
              onClickEventSourceID: sourceIDs.DETAILS,
            })
          }}
          quantity={quantity}
          size="medium"
          zeroQuantityText={
            hasMealTag(mealSummary.tags, TAG_IDS.FAMILY)
              ? 'Family Meal'
              : hasMealTag(mealSummary.tags, TAG_IDS.TWO_SERVINGS)
                ? 'Two-Pack'
                : ''
          }
        />
      )
    return (
      <MealDetailsDialog
        closeModal={closeDetailsDialog}
        imageTag={rating ? <MealRatingBadge stars={rating.stars} /> : undefined}
        meal={meal}
        orderStatus={
          <MenuFixedBottomContainer>
            <OrderStatus
              {...commonOrderStatusProps}
              onClickContinue={() => {
                closeDetailsDialog()
                menuTabs.selectTab('extras')
              }}
              onClickDone={(openSource) => {
                closeDetailsDialog()
                commonOrderStatusProps.onClickDone(openSource)
              }}
              onClickSkipAdjust={() => {
                closeDetailsDialog()
                onEditTermStatus(selectedUserTerm)
              }}
              openOrderSummary={() => {
                closeDetailsDialog()
                openOrderSummary()
              }}
            />
          </MenuFixedBottomContainer>
        }
        price={getMealSurcharge({ meal })}
        review={
          rating ? (
            <MealReviewCard
              mealID={meal.id}
              rating={rating}
              shouldScrollIntoView={shouldScrollToReview}
            />
          ) : null
        }
        stepper={stepper}
      />
    )
  }

  const renderMenuFilters = ({
    buttonStyle,
  }: {
    buttonStyle: 'link' | 'stroke' | 'white'
  }) => {
    if (buttonStyle === 'link') {
      return (
        <Button
          aria-label="Filter"
          buttonStyle="link"
          onClick={openFiltersSidebar}
          size="auto"
        >
          <div className="mr-2 h-6 w-6 md:mr-1">
            <FilterIcon />
          </div>
          <span className="text-k/16_125 md:text-k/14_120">
            Filter
            {selectedFiltersCount > 0 ? ` (${selectedFiltersCount})` : ''}
          </span>
        </Button>
      )
    }

    return (
      <Button
        buttonStyle={buttonStyle}
        onClick={openFiltersSidebar}
        size="small"
      >
        Edit Filter
      </Button>
    )
  }

  return (
    <>
      {selectedUserTerm.readyForView ? (
        <>
          {loadMenuError ? (
            <MenuError
              menuHeader={
                <MenuHeader
                  backButton={
                    <MyOrdersHeaderButton
                      beforeNavigate={() => {
                        track(events.MENU_BACK_CTA)
                      }}
                    />
                  }
                  filters={null}
                  isHeaderSticky={false}
                  selectedUserTerm={selectedUserTerm}
                />
              }
              subTermID={selectedUserTerm.selectedSubTermID}
              userID={user.id}
            />
          ) : (
            <MenuTabGroup
              commonMenuContent={
                <div className="mx-auto max-w-menu space-y-4">
                  {selectedUserTerm.isOnBreak && (
                    <div
                      className={clsx(
                        'mt-8 md:mt-0',
                        hasFlushMealsTop && 'mb-8 md:mb-4',
                      )}
                    >
                      <MenuOnBreakBanner />
                    </div>
                  )}

                  <div className="md:px-4">
                    <CustomerIOInlineContainer
                      className={clsx(
                        'mt-8 empty:mt-0 md:mt-4',
                        hasFlushMealsTop && 'mb-8 empty:mb-0 md:mb-4',
                      )}
                      elementID={`cio-menu-term-${selectedUserTerm.termID}`}
                    />
                  </div>
                </div>
              }
              mealsSort={
                <div className="mx-auto max-w-menu border-b border-grey-3 py-4">
                  <MealSort
                    menuSortOptions={menuSortOptions}
                    onChangeSelectedMealSort={onChangeSelectedMealSort}
                    selectedMealSort={selectedMealSort}
                    userID={user.id}
                  />
                </div>
              }
              menuHeader={({ isHeaderSticky }) => {
                return (
                  <MenuHeader
                    backButton={
                      <MyOrdersHeaderButton
                        beforeNavigate={() => {
                          track(events.MENU_BACK_CTA)

                          track(events.EXITS_MENU, {
                            meal_selection_count:
                              selectedUserTerm.mealSelections.length,
                            meal_selection_max: termMealCount,
                          })
                        }}
                      />
                    }
                    filters={
                      selectedMenuTab === 'meals'
                        ? renderMenuFilters({ buttonStyle: 'link' })
                        : null
                    }
                    isHeaderSticky={isHeaderSticky}
                    selectedUserTerm={selectedUserTerm}
                  />
                )
              }}
              menuTabPanels={{
                meals: (
                  <div>
                    {canDisplayMenu && (
                      <div>
                        <MenuMeals
                          everythingElseCollapse={
                            everythingElseCollapse
                              ? {
                                  hiddenMealsCount,
                                  onClickShowAll: onClickShowAllMeals,
                                }
                              : null
                          }
                          filteredGroupTheme={filteredGroupTheme}
                          filters={renderMenuFilters({
                            buttonStyle: filteredGroupTheme
                              ? 'white'
                              : 'stroke',
                          })}
                          mealGroups={displayMealGroups}
                          menuComponentsGrid={({
                            backgroundStyle,
                            components,
                          }) => {
                            return (
                              <MenuComponentsGrid
                                {...commonMenuComponentGridProps}
                                backgroundStyle={backgroundStyle}
                                components={components}
                              />
                            )
                          }}
                        />
                      </div>
                    )}
                  </div>
                ),
                extras: (
                  <div className="mt-10 pb-52 lg:px-4 md:mt-6 md:px-0 md:pb-40">
                    {menuHasListings ? (
                      <>
                        <ExtrasGrid
                          canModifySelections={canModifySelections}
                          disabledDecrement={!canDecrementListing}
                          disabledIncrement={!canIncrementListing}
                          listingsSelections={listingsSelections}
                          menuListingsLayout={menuListingsLayout}
                          onClickDecrement={onClickDecrementListing}
                          onClickIncrement={onClickIncrementListing}
                          onClickListing={(listingID: string) => {
                            openDetailsDialog({
                              id: listingID,
                              type: 'listing',
                            })

                            track(events.OPENS_EXTRA_DETAIL, {
                              listing_id: listingID,
                            })
                          }}
                        />

                        <div className="md:px-4">
                          <div className="mx-auto mt-16 max-w-[512px] rounded-lg bg-white px-10 py-4 md:px-4">
                            <ExtrasAllergenNote />
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="md:px-4">
                        <div className="mx-auto mt-16 max-w-[512px] rounded-lg bg-white px-10 py-4 md:px-4">
                          <div className="space-y-4 text-center md:text-left">
                            <h2 className="text-k/20_110">
                              No Extras This Week
                            </h2>
                            <p className="text-k/16_125 text-grey-9 md:text-body-sm">
                              Although you can usually find extras here, there
                              are none this week. Please check on another menu
                              to find extras.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ),
              }}
              menuTabs={menuTabs}
              tabChangeEventProps={{
                maxSelections: selectedUserTerm.isSkipped
                  ? (selectedUserTerm.subscriptionType?.maxSelections ?? 0)
                  : -1,
                selectionsCount: selectedUserTerm.isSkipped
                  ? selectedUserTerm.mealSelections.length +
                    pendingMealIDs.length
                  : -1,
                termID: selectedUserTerm.termID,
              }}
              // TODO: Remove this term ID conditional once no terms earlier than
              // 502 (first decoupled add ons term) are live. Before that term in
              // production, the extras count lives here on the tab rather than in
              // the order status coaching bar (see showExtrasCount in OrderStatus).
              totalListingSelections={
                getEnvVar('APP_ENV') === 'production' &&
                selectedUserTerm.termID < 502 &&
                !selectedUserTerm.isSkipped
                  ? listingsSelections.length
                  : 0
              }
            />
          )}
        </>
      ) : (
        <MenuUnavailable
          menuHeader={
            <MenuHeader
              backButton={<MyOrdersHeaderButton />}
              isHeaderSticky={false}
              selectedUserTerm={selectedUserTerm}
            />
          }
        />
      )}

      <MenuFixedBottomContainer
        banner={
          <CreditsBanner
            activeIncentives={activeIncentives}
            isFreeDessertCustomer={user.isFreeDessertCustomer}
            onClickBanner={() => setShowPromoCreditsModal(true)}
            totalCreditsCents={getTotalCreditsCents({ user })}
          />
        }
      >
        <OrderStatus {...commonOrderStatusProps} />
      </MenuFixedBottomContainer>

      {detailsType === 'meal' && meal
        ? renderMealDetailsDialog({ meal })
        : detailsType === 'listing' && listing
          ? renderListingDetailsDialog({ listing })
          : null}

      {orderSummarySidebarStatus.isOpen && (
        <OrderSummarySidebar
          hasOrderAgainMealsSelected={
            intersection(
              orderAgainMeals.map((orderAgainMeal) => orderAgainMeal.id),
              selectedUserTerm.mealSelections.map(
                (mealSelection) => mealSelection.mealID,
              ),
            ).length > 0
          }
          listings={selectedListings.map((listing) => {
            const quantity =
              listingsSelections.filter(
                (listingSelection) => listingSelection.listingID === listing.id,
              )?.length ?? 0
            return (
              <OrderSummaryExtra
                key={`order-summary-${listing.id}`}
                listing={listing}
                stepper={
                  <QuantityStepper
                    disabledDecrement={!canDecrementMeal}
                    disabledIncrement={!canIncrementMeal || listing.isSoldOut}
                    labelDecrement="Remove Meal"
                    labelIncrement="Add Meal"
                    min={0}
                    onClickDecrement={() => {
                      onClickDecrementListing({
                        listing,
                        onClickEventSourceID: sourceIDs.ORDER_SUMMARY,
                      })
                    }}
                    onClickIncrement={() => {
                      onClickIncrementListing({
                        listing,
                        onClickEventSourceID: sourceIDs.ORDER_SUMMARY,
                      })
                    }}
                    quantity={quantity}
                    size="small"
                  />
                }
              />
            )
          })}
          meals={selectedMeals.map((meal) => {
            const quantity = mealSelectionIDs.filter(
              (x) => x === meal.id,
            ).length

            return (
              <OrderSummaryMeal
                key={`order-summary-${meal.id}`}
                meal={meal}
                mealStepper={
                  <QuantityStepper
                    disabledDecrement={!canDecrementMeal}
                    disabledIncrement={!canIncrementMeal || meal.isSoldOut}
                    labelDecrement="Remove Meal"
                    labelIncrement="Add Meal"
                    min={0}
                    onClickDecrement={() => {
                      onClickDecrementMeal({
                        onClickEventSourceID: sourceIDs.ORDER_SUMMARY,
                        meal,
                      })
                    }}
                    onClickIncrement={() => {
                      onClickIncrementMeal({
                        onClickEventSourceID: sourceIDs.ORDER_SUMMARY,
                        meal,
                      })
                    }}
                    quantity={quantity}
                    size="small"
                  />
                }
              />
            )
          })}
          onClickPickMeals={() => {
            menuTabs.selectTab('meals')
            closeOrderSummary()
          }}
          onClickSkipAdjust={() => {
            onEditTermStatus(selectedUserTerm)
          }}
          onCloseSidebar={() => {
            closeOrderSummary()
          }}
          orderStatus={
            <MenuFixedBottomContainer>
              <OrderStatus {...commonOrderStatusProps} />
            </MenuFixedBottomContainer>
          }
          selectedUserTerm={selectedUserTerm}
          showOrderUpgrade={showOrderUpgrade}
        />
      )}

      {isSubscriptionActive && customer && !hasPaymentSource && (
        <div className="fixed bottom-0 left-0 right-0 flex flex-col items-center justify-center bg-white p-10">
          <h4 className="mb-4 text-k/20_125">
            Please add a payment method on the Account page to receive meals.
          </h4>

          <Button onClick={() => navigate('/account/payment')} size="large">
            Add Payment Method
          </Button>
        </div>
      )}
      {showRateMealsPrompt && (
        <div className="fixed bottom-2 left-2 z-20 max-w-[180px] lg:hidden">
          <RateMealsPrompt
            mostRecentTermMealsToRate={mostRecentTermMealsToRate}
            onClickClose={() => setShowRateMealsPrompt(false)}
          />
        </div>
      )}
      {isFiltersSidebarOpen && (
        <MenuFilters
          onClose={() => setIsFiltersSidebarOpen(false)}
          {...commonFiltersProps}
        />
      )}
      {showPromoCreditsModal && (
        <PromosCreditsModal
          onCloseModal={() => setShowPromoCreditsModal(false)}
          user={user}
        />
      )}
      {confirmationDialogData && (
        <ConfirmationDialogs
          confirmationDialogData={confirmationDialogData}
          onClickViewSummary={() => {
            setConfirmationDialogData(null)
            openOrderSummary('selectionsConfirmation')
          }}
          onClickWantMoreMeals={() => {
            if (menuHasListings) {
              navigate(`/menu/${selectedUserTerm.termID}`)
            }
          }}
          onClose={() => {
            setConfirmationDialogData(null)
          }}
          onDecrementMeal={decrementMeal}
          selectedUserTerm={selectedUserTerm}
          totalOrderCount={totalOrderCount}
        />
      )}
    </>
  )
}

export default Menu

function trackMealModified({
  maxSelections,
  mealID,
  mealIndex,
  numSelections,
  rating,
  termID,
}: {
  maxSelections: number
  mealID: number
  mealIndex: number | undefined
  numSelections: number
  rating: number | null
  termID: number
}) {
  track(events.MODIFY_MEAL_SELECTION, {
    meal_id: mealID,
    meal_index: mealIndex,
    rating,
    meal_selection_diff:
      maxSelections > 0 ? maxSelections - numSelections : undefined,
    meal_selection_count: numSelections,
    meal_selection_max: maxSelections,
    term_id: termID,
  })
}

const NoMenuTerm = ({
  message,
  selectedUserTerm,
}: {
  message: string
  selectedUserTerm: UserTerm
}) => {
  return (
    <div className="mx-auto max-w-menu space-y-6">
      <div className="pb-4 pt-10 lg:px-4 md:py-4">
        <MenuHeader
          backButton={
            <MyOrdersHeaderButton
              beforeNavigate={() => {
                track(events.MENU_BACK_CTA)
              }}
            />
          }
          filters={null}
          isHeaderSticky={false}
          selectedUserTerm={selectedUserTerm}
        />
      </div>
      <div className="md:px-4">
        <SpecialEventBanner>{message}</SpecialEventBanner>
      </div>
    </div>
  )
}

const MyOrdersHeaderButton = ({
  beforeNavigate,
}: {
  beforeNavigate?(): void
}) => {
  const navigate = useNavigate()

  return (
    <Button
      aria-label="My Orders"
      buttonStyle="link"
      onClick={() => {
        if (beforeNavigate) {
          beforeNavigate()
        }

        navigate('/my-orders')
      }}
      size="auto"
    >
      <div className="mr-2 h-6 w-6">
        <ArrowLeftIcon />
      </div>
      <span className="text-k/16_125 md:hidden">My Orders</span>
    </Button>
  )
}

const MenuOnBreakBanner = () => {
  return (
    <div className="rounded-xl bg-white md:rounded-none">
      <OnBreakBanner heading="Deliveries are paused this week">
        <p className="max-w-[366px] text-center md:max-w-[280px]">
          You're taking a break this week. Ready to jump back in? Resume
          deliveries below.
        </p>
      </OnBreakBanner>
    </div>
  )
}
